import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import CenterLayout from '../components/CenterLayout';

export default function AllRooms({ user }) {
  const [rooms, setRooms] = useState([]);
  const [pwModal, setPwModal] = useState({ open: false, room: null, input: '' });
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState('latest');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const navigate = useNavigate();

  // 모바일 화면 크기 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchRooms = async () => {
      const snap = await getDocs(collection(db, 'rooms'));
      setRooms(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };
    fetchRooms();
  }, []);

  const sortRooms = (rooms) => {
    switch (sortBy) {
      case 'latest':
        return [...rooms].sort((a, b) => (b.createdAt?.toDate?.() || 0) - (a.createdAt?.toDate?.() || 0));
      case 'oldest':
        return [...rooms].sort((a, b) => (a.createdAt?.toDate?.() || 0) - (b.createdAt?.toDate?.() || 0));
      case 'name':
        return [...rooms].sort((a, b) => (a.subject || '').localeCompare(b.subject || '', 'ko'));
      default:
        return rooms;
    }
  };

  const handleRoomClick = (room) => {
    if (room.password) {
      setPwModal({ open: true, room, input: '' });
      setError('');
    } else {
      navigate(`/rooms/${room.id}?pw=`);
    }
  };

  const handlePwSubmit = (e) => {
    e.preventDefault();
    if (pwModal.input === pwModal.room.password) {
      setPwModal({ open: false, room: null, input: '' });
      setError('');
      navigate(`/rooms/${pwModal.room.id}?pw=${encodeURIComponent(pwModal.input)}`);
    } else {
      setError('비밀번호가 올바르지 않습니다.');
    }
  };

  return (
    <CenterLayout>
      {/* 헤더 영역 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1.5rem',
        flexWrap: isMobile ? 'wrap' : 'nowrap',
        gap: isMobile ? '1rem' : '0',
        position: 'relative',
        background: 'white',
        padding: isMobile ? '1.2rem 1.5rem' : '1.5rem 2rem',
        borderRadius: '0.75rem',
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04)',
        overflow: 'hidden',
        border: '1px solid #f4f4f8'
      }}>
        <div style={{
          position: 'absolute',
          left: '0',
          top: '0',
          width: '6px',
          height: '100%',
          background: '#4f46e5'
        }}></div>
        <h2 style={{
          margin: 0,
          marginLeft: '6px',
          fontSize: isMobile ? '1.3rem' : '1.5rem',
          fontWeight: 600,
          color: '#111827',
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: isMobile ? '36px' : '42px',
            height: isMobile ? '36px' : '42px',
            borderRadius: '12px',
            background: '#f3f4ff',
            color: '#4f46e5'
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" width={isMobile ? "18" : "22"} height={isMobile ? "18" : "22"} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              <polyline points="9 22 9 12 15 12 15 22"></polyline>
            </svg>
          </div>
          전체 방 목록
        </h2>
        
        {/* 정렬 드롭다운 */}
        <div style={{
          position: 'relative',
          zIndex: 10,
          width: isMobile ? '100%' : 'auto',
        }}>
          <select 
            value={sortBy} 
            onChange={e => setSortBy(e.target.value)} 
            style={{
              width: '100%',
              padding: '0.7rem 1rem',
              fontSize: '0.95rem',
              borderRadius: '0.5rem',
              border: '1px solid #e2e8f0',
              background: 'white',
              appearance: 'none',
              backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 0.75rem center',
              backgroundSize: '1rem',
              paddingRight: '2.5rem',
              color: '#4b5563',
              boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            }}
          >
            <option value="latest">최신순</option>
            <option value="oldest">오래된순</option>
            <option value="name">이름순</option>
          </select>
        </div>
      </div>
      
      {/* 방 목록 그리드 */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: isMobile ? '1.2rem' : '1.5rem',
        marginBottom: isMobile ? '5rem' : '0',
        paddingBottom: '2rem',
      }}>
        {sortRooms(rooms).length > 0 ? (
          sortRooms(rooms).map(r => (
            <div
              key={r.id}
              onClick={() => handleRoomClick(r)}
              style={{
                background: r.password ? 'linear-gradient(135deg, #fffbeb 0%, #fff7ed 100%)' : 'white',
                borderRadius: '0.75rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                overflow: 'hidden',
                border: r.password ? '1px solid #fbbf24' : '1px solid #e2e8f0',
                transition: 'transform 0.2s, box-shadow 0.2s',
                cursor: 'pointer',
                WebkitTapHighlightColor: 'transparent',
                position: 'relative',
                padding: isMobile ? '1.25rem' : '1.25rem',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
            >
              {/* 방 정보 */}
              <div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '0.75rem',
                }}>
                  <h3 style={{
                    fontSize: isMobile ? '1.1rem' : '1.1rem',
                    fontWeight: 600,
                    margin: 0,
                    color: '#1e293b',
                    maxWidth: '85%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {r.subject}
                  </h3>
                  {r.password && (
                    <div style={{
                      flexShrink: 0,
                      width: isMobile ? '30px' : '28px',
                      height: isMobile ? '30px' : '28px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'rgba(251, 191, 36, 0.15)',
                      border: '1px solid rgba(251, 191, 36, 0.3)',
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e42" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="8" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                    </div>
                  )}
                </div>
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '0.75rem',
                  gap: '0.5rem',
                  flexWrap: 'wrap',
                }}>
                  <div style={{
                    fontSize: '0.85rem',
                    padding: '0.35rem 0.7rem',
                    borderRadius: '1rem',
                    background: 'rgba(99, 102, 241, 0.1)',
                    color: '#6366f1',
                    fontWeight: '500',
                    border: '1px solid rgba(99, 102, 241, 0.15)',
                  }}>
                    {r.day} {r.period}교시
                  </div>
                  <div style={{
                    fontSize: '0.85rem',
                    color: '#64748b',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {r.department || '-'}
                  </div>
                </div>
              </div>
              
              {/* 입장 버튼 */}
              <div style={{
                marginTop: 'auto',
                paddingTop: '0.75rem',
                borderTop: '1px solid #f1f5f9',
              }}>
                <button style={{
                  width: '100%',
                  padding: isMobile ? '0.75rem 0' : '0.65rem 0',
                  background: r.password ? 'transparent' : 'rgba(99, 102, 241, 0.05)',
                  border: r.password 
                    ? '1px solid #f59e42'
                    : '1px solid #6366f1',
                  color: r.password ? '#f59e42' : '#6366f1',
                  borderRadius: '0.5rem',
                  fontWeight: '600',
                  fontSize: '0.95rem',
                  cursor: 'pointer',
                }}>
                  {r.password ? '비밀번호 입력' : '입장하기'}
                </button>
              </div>
            </div>
          ))
        ) : (
          <div style={{
            gridColumn: '1 / -1',
            textAlign: 'center',
            padding: '3.5rem 1.5rem',
            background: 'white',
            borderRadius: '0.75rem',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            border: '1px solid #e2e8f0',
          }}>
            <div style={{
              marginBottom: '1.5rem',
              color: '#94a3b8',
              display: 'flex',
              justifyContent: 'center',
            }}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                <path d="M12 7v.01"/>
                <path d="M12 11v.01"/>
                <path d="M12 15v.01"/>
              </svg>
            </div>
            <p style={{
              margin: 0,
              fontSize: '1.1rem',
              fontWeight: 500,
              color: '#64748b',
            }}>등록된 방이 없습니다</p>
            <p style={{
              margin: '0.5rem 0 0 0', 
              fontSize: '0.95rem',
              color: '#94a3b8',
            }}>
              새로운 방을 생성하거나 나중에 다시 확인해주세요
            </p>
          </div>
        )}
      </div>
      
      {/* 비밀번호 입력 모달 */}
      {pwModal.open && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          backdropFilter: 'blur(4px)',
          padding: '1rem',
        }} onClick={()=>setPwModal({open:false,room:null,input:''})}>
          <div style={{
            background: 'white',
            borderRadius: '0.75rem',
            width: '100%',
            maxWidth: '350px',
            padding: '1.5rem',
            boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
          }} onClick={e=>e.stopPropagation()}>
            <h3 style={{
              margin: 0,
              marginBottom: '1.5rem',
              fontSize: '1.25rem',
              fontWeight: 600,
              color: '#1e293b',
            }}>비밀번호 입력</h3>
            
            <form onSubmit={handlePwSubmit}>
              <input
                type="password"
                placeholder="방 비밀번호를 입력하세요"
                value={pwModal.input}
                onChange={e=>setPwModal({...pwModal, input:e.target.value})}
                autoFocus
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #e2e8f0',
                  fontSize: '1rem',
                  marginBottom: error ? '0.5rem' : '1.5rem',
                }}
              />
              
              {error && (
                <div style={{
                  color: '#ef4444',
                  fontSize: '0.9rem',
                  marginBottom: '1.5rem',
                }}>
                  {error}
                </div>
              )}
              
              <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
                <button 
                  type="submit" 
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    background: '#6366f1',
                    color: 'white',
                    border: 'none',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  입장하기
                </button>
                
                <button 
                  type="button"
                  onClick={()=>setPwModal({open:false,room:null,input:''})}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '0.5rem',
                    background: 'transparent',
                    border: '1px solid #cbd5e1',
                    color: '#64748b',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  취소
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </CenterLayout>
  );
} 